import { BookableCourt, BookingSlot } from './types';

/**
 * Slots a player may add to the current selection.
 *
 * A booking is one unbroken stretch on one court, so only the chips touching
 * either end of what is already picked stay selectable. With nothing picked,
 * every available chip is open.
 */
export const getSelectableSlotIds = (slots: BookingSlot[], selectedIds: string[]): Set<string> => {
  const available = slots.filter((slot) => slot.isAvailable);

  if (selectedIds.length === 0) {
    return new Set(available.map((slot) => slot.id));
  }

  const selected = new Set(selectedIds);
  const indexes = available.reduce<number[]>((acc, slot, index) => {
    if (selected.has(slot.id)) acc.push(index);
    return acc;
  }, []);

  const first = indexes[0];
  const last = indexes[indexes.length - 1];
  const ids = new Set(selectedIds);

  // Only neighbours that are themselves adjacent in wall-clock time extend the
  // run — an unavailable slot in between leaves a gap the club cannot sell.
  const before = available[first - 1];
  if (before && before.endAt === available[first].startAt) ids.add(before.id);

  const after = available[last + 1];
  if (after && after.startAt === available[last].endAt) ids.add(after.id);

  return ids;
};

/**
 * Apply a tap to the selection.
 *
 * Tapping inside a run would split it, so re-tapping anything other than an end
 * restarts the selection from that chip.
 */
export const toggleSlotSelection = (
  slots: BookingSlot[],
  selectedIds: string[],
  slotId: string,
): string[] => {
  const ordered = slots.filter((slot) => selectedIds.includes(slot.id)).map((slot) => slot.id);

  if (!ordered.includes(slotId)) {
    const next = [...ordered, slotId];
    return slots.filter((slot) => next.includes(slot.id)).map((slot) => slot.id);
  }

  const isEdge = ordered[0] === slotId || ordered[ordered.length - 1] === slotId;
  if (isEdge) {
    return ordered.filter((id) => id !== slotId);
  }

  return [slotId];
};

export const getSelectedSlots = (slots: BookingSlot[], selectedIds: string[]) =>
  slots.filter((slot) => selectedIds.includes(slot.id));

export const getSelectedMinutes = (slots: BookingSlot[], selectedIds: string[]) =>
  getSelectedSlots(slots, selectedIds).reduce((total, slot) => {
    const minutes = (new Date(slot.endAt).getTime() - new Date(slot.startAt).getTime()) / 60_000;
    return total + minutes;
  }, 0);

/**
 * The selection rendered as one range, e.g. `8 – 9 PM`.
 *
 * Takes the first chip's start and the last chip's end rather than joining
 * labels, so three half-hour chips read as a single 90-minute booking.
 */
export const getSelectionTimeLabel = (slots: BookingSlot[], selectedIds: string[]) => {
  const selected = getSelectedSlots(slots, selectedIds);
  if (selected.length === 0) {
    return '';
  }

  const [first] = selected;
  const last = selected[selected.length - 1];
  const start = first.label.split('–')[0].trim();
  const end = last.label.split('–')[1]?.trim() ?? '';

  return end ? `${start} – ${end}` : first.label;
};

/** Hourly rate × selected duration. Cents in, cents out. */
export const getCourtTotalUnitAmount = (court: BookableCourt, minutes: number) =>
  Math.round((court.priceUnitAmount * minutes) / 60);

/** True when the club sells this stretch below list price. */
export const getIsDiscounted = (court: BookableCourt) =>
  court.listPriceUnitAmount !== null && court.listPriceUnitAmount > court.priceUnitAmount;

const formatLocalTime = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  const suffix = hours >= 12 && hours < 24 ? 'PM' : 'AM';
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0
    ? `${display} ${suffix}`
    : `${display}:${String(minutes).padStart(2, '0')} ${suffix}`;
};

/**
 * `10 – 10:30 AM`, from venue-local HH:mm — the meridiem is dropped from the
 * start when both ends share it, which is how the design renders every chip.
 */
export const slotLabel = (startLocal: string, endLocal: string) => {
  const startText = formatLocalTime(startLocal);
  const endText = formatLocalTime(endLocal);
  const [startValue, startSuffix] = startText.split(' ');
  const [, endSuffix] = endText.split(' ');
  return startSuffix === endSuffix ? `${startValue} – ${endText}` : `${startText} – ${endText}`;
};
